import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import TimeSelector from '@/components/selectors/TimeSelector';
import { useFormField } from '@core/hooks/useFormField';

type ValidationRule = (value: any) => string;

export interface FormTimePickerProps {
    name: string;
    value: DateTime | null;
    rules?: ValidationRule[];
    validateOn?: 'blur' | 'change' | 'both';
    helperText?: string;
    label?: string;
    hideLabel?: boolean;
    disabled?: boolean;
    ampm?: boolean;
    minutesStep?: number;
    onChange?: (value: DateTime | null) => void;
}

const FormTimePicker: React.FC<FormTimePickerProps> = ({
    name,
    value,
    rules = [],
    validateOn = 'both',
    helperText: staticHelperText,
    label,
    hideLabel,
    disabled = false,
    minutesStep = 5,
    onChange
}) => {
    const { error, showError, handleBlur, handleChange } = useFormField({
        name,
        label,
        value,
        rules,
        validateOn,
        onChange
    });

    const timeValue = value?.isValid ? value.toFormat('HH:mm') : '';

    const handleTimeChange = useCallback(
        (time: string) => {
            const parts = time.split(':');
            const hour = Number.parseInt(parts[0], 10);
            const minute = Number.parseInt(parts[1], 10);
            if (Number.isNaN(hour) || Number.isNaN(minute)) return;

            const base = value?.isValid ? value : DateTime.local();
            const newValue = base.set({ hour, minute, second: 0, millisecond: 0 });

            handleChange(newValue);
            handleBlur();
        },
        [value, handleChange, handleBlur]
    );

    return (
        <Box data-form-field={name} className={`form-time-picker-wrapper${showError ? ' form-time-picker-error' : ''}`}>
            <TimeSelector
                value={timeValue}
                onChange={handleTimeChange}
                label={hideLabel ? '' : label}
                disabled={disabled}
                minutesStep={minutesStep}
            />
            {(showError ? error : staticHelperText) && (
                <Box className={`selector-helper${showError ? ' error' : ''}`}>
                    {showError ? error : staticHelperText}
                </Box>
            )}
        </Box>
    );
};

FormTimePicker.displayName = 'FormTimePicker';

export default FormTimePicker;
